<template>
  <div class="position-relative h-100 w-100">
    <div class="map-header position-absolute text-light">
      <h2 class="h3 m-0"><span>Day 11 - </span>3D</h2>
    </div>
    <div ref="map-root" class="basemap"></div>
  </div>
</template>

<script>
import mapboxgl from "mapbox-gl";
import "mapbox-gl/src/css/mapbox-gl.css";

export default {
  data() {
    return {
      accessToken:
        "pk.eyJ1Ijoia21hcnNoYWxsIiwiYSI6ImNrdXdudGlpODI2ejkydXF2bXcyM3poaWQifQ.hyBCr6uwWlDGMGyVmuKCIw",
      map: null,
    };
  },
  methods: {
    updateCameraPosition(position, altitude, target) {
      const camera = this.map.getFreeCameraOptions();

      camera.position = mapboxgl.MercatorCoordinate.fromLngLat(
        position,
        altitude
      );
      camera.lookAtPoint(target);

      this.map.setFreeCameraOptions(camera);
    },
    initMap() {
      mapboxgl.accessToken = this.accessToken;

      this.map = new mapboxgl.Map({
        container: this.$refs["map-root"],
        zoom: 11.53,
        center: [-3.2258605957031246, 55.95342878289969],
        pitch: 96,
        bearing: 0,
        style: "mapbox://styles/mapbox/satellite-v9",
        interactive: false,
      });

      // add terrain and sky layers once the style has loaded
      this.map.on("load", () => {
        this.map.addSource("mapbox-dem", {
          type: "raster-dem",
          url: "mapbox://mapbox.mapbox-terrain-dem-v1",
          tileSize: 512,
          maxzoom: 14,
        });
        this.map.setTerrain({ source: "mapbox-dem", exaggeration: 1.5 });

        this.map.addLayer({
          id: "sky",
          type: "sky",
          paint: {
            "sky-type": "atmosphere",
            "sky-atmosphere-sun": [0.0, 90.0],
            "sky-atmosphere-sun-intensity": 15,
          },
        });
      });

      let animationIndex = 0;
      let animationTime = 0.0;

      // wait for the terrain and sky to load before starting animations
      this.map.once("idle", () => {
        // linearly interpolate between two altitudes/positions based on time
        const lerp = (a, b, t) => {
          if (Array.isArray(a) && Array.isArray(b)) {
            const result = [];
            for (let i = 0; i < Math.min(a.length, b.length); i++)
              result[i] = a[i] * (1.0 - t) + b[i] * t;
            return result;
          } else {
            return a * (1.0 - t) + b * t;
          }
        };

        const animations = [
          {
            duration: 20000.0,
            animate: (phase) => {
              const start = [-3.1354808807373047, 55.940307890739426];
              const end = [-3.2067203521728516, 55.94871923055638];
              const alt = [1000.0, 1000.0];

              // interpolate camera position while keeping focus on a target lat/lng
              const position = lerp(start, end, phase);
              const altitude = lerp(alt[0], alt[1], phase);
              const target = [-3.161916732788086, 55.944153301340144];

              this.updateCameraPosition(position, altitude, target);
            },
          },
          {
            duration: 15000.0,
            animate: (phase) => {
              const start = [-3.2100677490234375, 55.95073768027848];
              const end = [-3.2171058654785156, 55.95049739416244];
              const alt = [1000.0, 1000.0];
              const target1 = [-3.161916732788086, 55.944153301340144];
              const target2 = [-3.1850051879882812, 55.95270797018195];

              // interpolate both the camera position and target
              const position = lerp(start, end, phase);
              const altitude = lerp(alt[0], alt[1], phase);
              const target = lerp(target1, target2, phase);

              this.updateCameraPosition(position, altitude, target);
            },
          },
          {
            duration: 15000.0,
            animate: (phase) => {
              // create easing function for the animation
              const easeInOutQuad = (t) => {
                return t < 0.5 ? 2.0 * t * t : (4.0 - 2.0 * t) * t - 1.0;
              };
              const start = [-3.2171058654785156, 55.95049739416244];
              const end = [-3.1354808807373047, 55.940307890739426];
              const alt = [1000.0, 1000.0];
              const target1 = [-3.1850051879882812, 55.95270797018195];
              const target2 = [-3.161916732788086, 55.944153301340144];

              // interpolate both the camera position and target
              const position = lerp(start, end, easeInOutQuad(phase));
              const altitude = lerp(alt[0], alt[1], phase);
              const target = lerp(target1, target2, phase);

              this.updateCameraPosition(position, altitude, target);
            },
          },
        ];

        let lastTime = 0.0;
        function frame(time) {
          animationIndex %= animations.length;
          const current = animations[animationIndex];

          if (animationTime < current.duration) {
            // Normalize the duration between 0 and 1 to interpolate the animation
            const phase = animationTime / current.duration;
            current.animate(phase);
          }

          // Elasped time since last frame, in milliseconds
          const elapsed = time - lastTime;
          animationTime += elapsed;
          lastTime = time;

          if (animationTime > current.duration) {
            animationIndex++;
            animationTime = 0.0;
          }

          window.requestAnimationFrame(frame);
        }

        window.requestAnimationFrame(frame);
      });
    },
  },
  mounted() {
    this.initMap();
  },
};
</script>

<style lang="scss" scoped>
@import "../assets/scss/map-shared.scss";
</style>
